import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ArrowLeft, Plus, Upload, Download, Trash2, RotateCcw, Mail,
  School as SchoolIcon, AlertCircle, CheckCircle2, XCircle,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { v: 'active', label: 'Active' },
  { v: 'transferred', label: 'Transferred' },
  { v: 'graduated', label: 'Graduated' },
  { v: 'inactive', label: 'Inactive' },
];

export default function RosterPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [school, setSchool] = useState(null);
  const [cls, setCls] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('enrolled');
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ name: '', student_email: '', student_dob: '', parent_email: '' });
  const [saving, setSaving] = useState(false);
  const [bulkErrors, setBulkErrors] = useState(null);
  const [bulkSuccess, setBulkSuccess] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);  // pending invite
  const [revokingId, setRevokingId] = useState(null);
  // Add Parent Email dialog state (v1: add-only, no edit).
  const [parentEmailOpen, setParentEmailOpen] = useState(null);  // enrollment object or null
  const [parentEmailValue, setParentEmailValue] = useState('');
  const [parentEmailSaving, setParentEmailSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      const schoolId = meRes.data?.school_id;
      if (!schoolId) return;
      const [schoolRes, clsRes, enrRes, invRes] = await Promise.all([
        api.get(`/schools/${schoolId}`),
        api.get(`/schools/${schoolId}/classes?status=active`),
        api.get(`/schools/${schoolId}/classes/${classId}/enrollments`),
        api.get(`/schools/${schoolId}/classes/${classId}/enrollments/invites`),
      ]);
      setSchool(schoolRes.data);
      setCls((clsRes.data || []).find((c) => c.school_class_id === classId) || null);
      setEnrollments(enrRes.data || []);
      setPending(invRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => { load(); }, [load]);

  const schoolId = me?.school_id;
  const brandColor = school?.brand_color || '#4F46E5';

  const submitAdd = async () => {
    if (!form.name.trim() || !form.student_email.trim()) {
      toast.error('Name and Student Email are required'); return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        student_email: form.student_email.trim(),
      };
      if (form.student_dob) body.student_dob = form.student_dob;
      if (form.parent_email) body.parent_email = form.parent_email.trim();
      await api.post(`/schools/${schoolId}/classes/${classId}/enrollments`, body);
      toast.success('Enrollment created. Invites sent.');
      setAddOpen(false);
      setForm({ name: '', student_email: '', student_dob: '', parent_email: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to enroll');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpload = async (file) => {
    setBulkErrors(null);
    setBulkSuccess(null);
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(
        `/schools/${schoolId}/classes/${classId}/enrollments/bulk`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setBulkSuccess(res.data);
      toast.success(`Enrolled ${res.data.enrolled} students`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        setBulkErrors(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Bulk upload failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(
        `/schools/${schoolId}/classes/${classId}/enrollments/template.xlsx`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yukti-students-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const updateStatus = async (eid, status) => {
    try {
      await api.patch(`/schools/${schoolId}/enrollments/${eid}`, { status });
      toast.success(`Updated to ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const resendInvites = async (eid) => {
    try {
      await api.post(`/schools/${schoolId}/enrollments/${eid}/resend`);
      toast.success('Invites re-sent');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const openAddParentEmail = (enrollment) => {
    setParentEmailValue('');
    setParentEmailOpen(enrollment);
  };

  const submitParentEmail = async () => {
    if (!parentEmailOpen) return;
    const email = parentEmailValue.trim().toLowerCase();
    if (!email) {
      toast.error('Parent email is required');
      return;
    }
    setParentEmailSaving(true);
    try {
      await api.post(
        `/schools/${schoolId}/enrollments/${parentEmailOpen.enrollment_id}/parent-email`,
        { parent_email: email },
      );
      toast.success('Parent invite sent');
      setParentEmailOpen(null);
      setParentEmailValue('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add parent email');
    } finally {
      setParentEmailSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setRevokingId(confirmRevoke.invite_id);
    try {
      await api.post(`/invites/${confirmRevoke.invite_id}/revoke`);
      toast.success(`Invite for ${confirmRevoke.email} revoked.`);
      setConfirmRevoke(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke invite');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="roster-page"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo_url ? (
              <img src={school.logo_url} alt={school.name} className="w-10 h-10 rounded-lg object-contain bg-white border" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: brandColor }}>
                <SchoolIcon size={20} />
              </div>
            )}
            <div>
              <h1 className="font-bold text-slate-900">{school?.name}</h1>
              <p className="text-xs text-slate-500">{me?.email} · School Admin</p>
            </div>
          </div>
          <Link to="/school-admin" className="text-sm text-indigo-600 hover:underline">
            ← Back to dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900" data-testid="roster-class-name">
              {cls?.name || 'Class'} Student List
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Grade {cls?.grade}{cls?.section ? ` · Section ${cls.section}` : ''} · {cls?.academic_year}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setBulkOpen(true)}
              variant="outline" size="sm"
              data-testid="roster-bulk-upload-btn"
            >
              <Upload size={14} className="mr-1.5" /> Bulk Upload
            </Button>
            <Button
              onClick={() => setAddOpen(true)}
              style={{ backgroundColor: brandColor }}
              className="text-white"
              data-testid="roster-add-student-btn"
            >
              <Plus size={14} className="mr-1.5" /> Add Student
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 bg-white border" data-testid="roster-tabs">
            <TabsTrigger value="enrolled" data-testid="roster-tab-enrolled">
              Enrolled ({enrollments.filter((e) => e.status === 'active').length})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="roster-tab-pending">
              Pending Invites ({pending.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enrolled">
            {enrollments.length === 0 ? (
              <div className="border-2 border-dashed rounded-xl py-16 text-center bg-white/60" data-testid="roster-empty">
                <p className="text-slate-700 font-semibold">No enrollments yet</p>
                <p className="text-sm text-slate-500 mt-1">
                  Add students manually or via bulk upload.
                </p>
              </div>
            ) : (
              <div className="bg-white border rounded-xl divide-y" data-testid="roster-list">
                {enrollments.map((e) => (
                  <div
                    key={e.enrollment_id}
                    className="px-4 py-3 flex items-center justify-between gap-3"
                    data-testid={`enrollment-row-${e.enrollment_id}`}
                  >
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{e.student_name}</div>
                      <div className="text-xs text-slate-500">
                        {e.student_email}
                        {e.parent_email ? (
                          <span> · Parent: {e.parent_email}</span>
                        ) : (
                          <>
                            <span> · </span>
                            <button
                              type="button"
                              onClick={() => openAddParentEmail(e)}
                              className="text-indigo-600 hover:underline font-medium"
                              data-testid={`add-parent-email-${e.enrollment_id}`}
                            >
                              + Add parent email
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <Badge variant={e.status === 'active' ? 'default' : 'secondary'}>
                      {e.status}
                    </Badge>
                    <Select
                      value={e.status}
                      onValueChange={(v) => updateStatus(e.enrollment_id, v)}
                    >
                      <SelectTrigger
                        className="w-[140px]"
                        data-testid={`enrollment-status-select-${e.enrollment_id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => resendInvites(e.enrollment_id)}
                      data-testid={`enrollment-resend-${e.enrollment_id}`}
                      title="Resend invites"
                    >
                      <Mail size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="pending">
            {pending.length === 0 ? (
              <div className="border-2 border-dashed rounded-xl py-12 text-center bg-white/60" data-testid="pending-empty">
                <p className="text-slate-600">No pending invites for this class.</p>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl divide-y divide-amber-200" data-testid="pending-list">
                {pending.map((p) => (
                  <div
                    key={p.invite_id}
                    className="px-4 py-3 flex items-center justify-between gap-3"
                    data-testid={`pending-invite-row-${p.invite_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{p.email}</div>
                      <div className="text-xs text-slate-500 capitalize">
                        {p.role} invite · expires {new Date(p.expires_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge variant="secondary" className="capitalize">{p.role}</Badge>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setConfirmRevoke(p)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`pending-invite-revoke-${p.invite_id}`}
                      title="Revoke this pending invite"
                    >
                      <XCircle size={14} className="mr-1" /> Revoke
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-enrollment-dialog">
          <DialogHeader>
            <DialogTitle>Add a student</DialogTitle>
            <DialogDescription>
              We'll email a sign-in invite to the student. If you provide a parent email,
              they'll get a separate invite for read-only access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="add-student-name">Full Name *</Label>
              <Input
                id="add-student-name"
                data-testid="add-student-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="add-student-email">Student Email *</Label>
              <Input
                id="add-student-email"
                type="email"
                data-testid="add-student-email"
                value={form.student_email}
                onChange={(e) => setForm({ ...form, student_email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="add-student-dob">Date of Birth (optional)</Label>
              <Input
                id="add-student-dob"
                type="date"
                data-testid="add-student-dob"
                value={form.student_dob}
                onChange={(e) => setForm({ ...form, student_dob: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="add-student-parent-email">Parent Email (optional)</Label>
              <Input
                id="add-student-parent-email"
                type="email"
                data-testid="add-student-parent-email"
                value={form.parent_email}
                onChange={(e) => setForm({ ...form, parent_email: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving} data-testid="add-student-cancel">Cancel</Button>
            <Button
              onClick={submitAdd} disabled={saving}
              style={{ backgroundColor: brandColor }} className="text-white"
              data-testid="add-student-submit"
            >
              {saving ? 'Saving…' : 'Enroll & Send Invites'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkErrors(null); setBulkSuccess(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="bulk-enroll-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Enroll Students</DialogTitle>
            <DialogDescription>
              Upload a .xlsx file. Validation is strict — any bad row blocks the entire batch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Button
              onClick={downloadTemplate}
              variant="outline" size="sm"
              data-testid="roster-template-download"
            >
              <Download size={14} className="mr-1.5" /> Download Template
            </Button>
            <div>
              <Label htmlFor="bulk-file">Upload Student List (.xlsx)</Label>
              <Input
                id="bulk-file"
                type="file"
                accept=".xlsx"
                data-testid="bulk-file-input"
                onChange={(e) => {
                  if (e.target.files?.[0]) handleBulkUpload(e.target.files[0]);
                }}
              />
            </div>
            {bulkErrors && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-60 overflow-auto" data-testid="bulk-errors">
                <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                  <AlertCircle size={16} /> {bulkErrors.message}
                </div>
                <ul className="text-xs text-red-700 space-y-1">
                  {bulkErrors.errors.map((er, i) => (
                    <li key={i}>
                      Row {er.row}: {er.email || er.student_email || er.name} — {er.errors.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {bulkSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="bulk-success">
                <div className="flex items-center gap-2 text-green-700 font-semibold">
                  <CheckCircle2 size={16} /> Enrolled {bulkSuccess.enrolled} student(s)
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke pending invite confirmation */}
      <Dialog open={confirmRevoke !== null} onOpenChange={(v) => { if (!v) setConfirmRevoke(null); }}>
        <DialogContent data-testid="roster-invite-revoke-dialog">
          {confirmRevoke && (
            <>
              <DialogHeader>
                <DialogTitle>Revoke {confirmRevoke.role} invite?</DialogTitle>
                <DialogDescription>
                  The link emailed to <b>{confirmRevoke.email}</b> will stop working.
                  They won't be able to accept anymore. You can re-invite them later
                  using <b>Resend</b> from the Enrolled tab if needed.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmRevoke(null)}
                  disabled={revokingId === confirmRevoke.invite_id}
                  data-testid="roster-invite-revoke-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRevoke}
                  disabled={revokingId === confirmRevoke.invite_id}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="roster-invite-revoke-confirm"
                >
                  {revokingId === confirmRevoke.invite_id ? 'Revoking…' : 'Revoke invite'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Parent Email dialog (v1: add-only) */}
      <Dialog
        open={!!parentEmailOpen}
        onOpenChange={(o) => { if (!o && !parentEmailSaving) setParentEmailOpen(null); }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="add-parent-email-dialog"
        >
          <DialogHeader>
            <DialogTitle>Add parent email</DialogTitle>
            <DialogDescription>
              {parentEmailOpen?.student_name
                ? `Send a parent-account invite for ${parentEmailOpen.student_name}.`
                : 'Send a parent-account invite for this student.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="add-parent-email-input">Parent email</Label>
            <Input
              id="add-parent-email-input"
              type="email"
              placeholder="parent@example.com"
              value={parentEmailValue}
              onChange={(ev) => setParentEmailValue(ev.target.value)}
              autoFocus
              data-testid="add-parent-email-input"
            />
            <p className="text-xs text-slate-500">
              An invite will be sent immediately. The parent can accept and link to this student.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { if (!parentEmailSaving) setParentEmailOpen(null); }}
              disabled={parentEmailSaving}
              data-testid="add-parent-email-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitParentEmail}
              disabled={parentEmailSaving || !parentEmailValue.trim()}
              data-testid="add-parent-email-submit"
            >
              {parentEmailSaving ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
